//#region react import
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useMutation } from "@tanstack/react-query";
//#endregion

//#region components
import GstaModal from "../../../Components/GstaModal/GstaModal.component";
import GstaInput from "../../../Components/GstaInput/GstaInput.component";
import DependentSelectRow from "./DependentSelectRow";
//#endregion

//#region service import
import {
  createCustomTrainingGroup,
  updateCustomTrainingGroup,
  getAvailableTrainings,
  getFormationDetails,
} from "../../../Services/SessionsService";
//#endregion

//#region helpers
import { getTranslations } from "../../../Helper/TranslationController";
//#endregion

//#region constants
import { TRANSLATIONS } from "../../../Constants/Translations";
import { INPUT_TEXT } from "../../../Components/GstaInput/GstaInput.constants";
import {
  getAvailableMachineOptions as getAvailableMachineOptionsUtil,
  getTrainingOptions as getTrainingOptionsUtil,
  createTrainingMachineList as createTrainingMachineListUtil,
  handleChangeMachine as handleChangeMachineUtil,
  handleChangeTraining as handleChangeTrainingUtil,
  handleAddBlock as handleAddBlockUtil,
  handleRemoveBlock as handleRemoveBlockUtil,
} from "./CreateFormationForm.constant";
//#endregion

//#region store import
import { setErrorNotification, setSuccessNotification } from "../../../ReduxStore/notificationSlice";
//#endregion

//#region style
import "./CreateFormationForm.style.scss";
//#endregion

const CreateFormationForm = ({ modalOpen, closeModal, refetchFormations, formationId = null }) => {
  //#region stores
  const translations = useSelector((state) => state.translationSlice.translations);
  const activeCenterId = useSelector((state) => state.connexionSlice.trainer?.activeCenter?.id);
  const dispatch = useDispatch();
  //#endregion

  //#region states
  const [formationName, setFormationName] = useState(null);
  const [machineTrainingSections, setMachineTrainingSections] = useState([{ machineId: null, trainingId: null }]);
  //#endregion

  //#region useQueries
  const { data: machines = [] } = useQuery({
    queryKey: ["machineList", activeCenterId, translations?.locale || ""],
    queryFn: () => getAvailableTrainings(activeCenterId),
    enabled: !!activeCenterId && !!translations,
    select: (availableMachines) =>
      (availableMachines || []).map((machine) => ({
        id: machine.id,
        name: machine.name,
        code: machine.code,
        trainings: (machine.trainings || []).map((training) => ({
          id: training.combinedTrainingId,
          code: training.code,
          name: training.name,
        })),
      })),
  });

  const { data: formationDetails } = useQuery({
    queryKey: ["formationDetails", formationId, activeCenterId],
    queryFn: () => getFormationDetails(activeCenterId, formationId),
    enabled: !!formationId && !!activeCenterId && modalOpen,
  });

  const { mutate: saveFormationMutate } = useMutation({
    mutationFn: (machineTrainingDetails) => {     
      if (formationId) {
        const formatedTraining = {
          id: formationId,
          name: formationName?.trim(),
          isArchived: formationDetails?.isArchived || false,
          isCustom: formationDetails?.isCustom || false,
          ...machineTrainingDetails,
        };
        return updateCustomTrainingGroup(activeCenterId, formationId, formatedTraining);
      } else {
        return createCustomTrainingGroup(activeCenterId, formationName?.trim(), machineTrainingDetails);
      }
    },
    onSuccess: () => {
      refetchFormations();
      const message = formationId
        ? getTranslations(TRANSLATIONS.CUSTOM_TRAINING_UPDATED_CONFIRM, translations)
        : getTranslations(TRANSLATIONS.CUSTOM_TRAINING_CREATED_CONFIRM, translations);
      dispatch(setSuccessNotification(message));
      closeModal();
    },
    onError: (error) => {
      dispatch(setErrorNotification(error));
    },
  });
  //#endregion

  //#region useMemos
  const machineOptions = useMemo(
    () => machines.map((machine) => ({ value: machine.id, name: machine.name, code: machine.code })),
    [machines]
  );
  const showDeleteButton = useMemo(
    () => machineTrainingSections.length > 1 || machineTrainingSections.some((section) => section.machineId),
    [machineTrainingSections]
  );

  const isValidateDisabled = useMemo(() => {
    if (!formationName || formationName.trim().length === 0) return true;
    for (const section of machineTrainingSections) {
      if (
        !section.machineId ||
        !section.trainingId ||
        (Array.isArray(section.trainingId) && section.trainingId.length === 0)
      ) {
        return true;
      }
    }
    return false;
  }, [formationName, machineTrainingSections]);

  //#endregion

  //#region useEffects
  useEffect(() => {
    if (!modalOpen) {
      setFormationName(null);
      setMachineTrainingSections([{ machineId: null, trainingId: null }]);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen) {
      if (formationDetails && formationId) {
        setFormationName(formationDetails.name);
        const sections = formationDetails.machines.map((machine) => ({
          machineId: machine.id,
          trainingId: machine.trainings.map((t) => t.combinedTrainingId),
        }));
        setMachineTrainingSections(sections.length > 0 ? sections : [{ machineId: null, trainingId: null }]);
      } else if (!formationId) {
        setFormationName(null);
        setMachineTrainingSections([{ machineId: null, trainingId: null }]);
      }
    }
  }, [formationDetails, formationId, modalOpen]);
  //#endregion

  //#region handlers
  const handleChangeMachine = (index, id) => handleChangeMachineUtil(index, id, setMachineTrainingSections);

  const handleChangeTraining = (index, id) => handleChangeTrainingUtil(index, id, setMachineTrainingSections);

  const handleAddBlock = (index) => handleAddBlockUtil(index, setMachineTrainingSections);

  const handleRemoveBlock = (index) => handleRemoveBlockUtil(index, setMachineTrainingSections);

  const handleValidate = () => {
    const machineTrainingDetails = createTrainingMachineListUtil(machineTrainingSections, machines);
    saveFormationMutate(machineTrainingDetails);
  };
  //#endregion

  return (
    <GstaModal
      cancelText={getTranslations(TRANSLATIONS.COMMON_CANCEL, translations)}
      validText={getTranslations(TRANSLATIONS.COMMON_VALIDATE, translations)}
      modalOpen={modalOpen}
      handleCloseModal={closeModal}
      title={
        formationId
          ? getTranslations(TRANSLATIONS.GSTA_BUTTON_FORMATION_EDIT, translations)
          : getTranslations(TRANSLATIONS.GSTA_BUTTON_FORMATION_ADD, translations)
      }
      handleValidate={handleValidate}
      noFooter
      validateDisabled={isValidateDisabled}
      className="create-formation"
    >
      <form
        className="create-formation-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleValidate();
        }}
      >
        <article>
          <GstaInput
            label={getTranslations(TRANSLATIONS.GSTA_FORMATION_NAME, translations)}
            placeholder={getTranslations(TRANSLATIONS.GSTA_FORMATION_NAME_EXEMPLE, translations)}
            value={formationName}
            setValue={setFormationName}
            appearanceClass="gsta-input_column gsta-input--black custom-field--width"
            type={INPUT_TEXT}
            isValid
          />
        </article>
        <span className="label-select">{getTranslations(TRANSLATIONS.GSTA_FORMATION_MACHINES, translations)}</span>
        <section className="scrollable-section">
          <div className="create-formation-form_machines-selection">
            {machineTrainingSections.map((block, idx) => (
              <DependentSelectRow
                key={idx}
                rowIndex={idx}
                rowData={block}
                machineOptions={getAvailableMachineOptionsUtil(machineTrainingSections, idx, machineOptions)}
                getTrainingOptions={(machineId) => getTrainingOptionsUtil(machineId, machines)}
                translations={translations}
                onMachineSelect={handleChangeMachine}
                onTrainingSelect={handleChangeTraining}
                onAddRow={handleAddBlock}
                onDeleteRow={handleRemoveBlock}
                showDeleteButton={showDeleteButton}
              />
            ))}
          </div>
        </section>
      </form>
    </GstaModal>
  );
};

export default CreateFormationForm;
